import { createGameStore } from '../lib/createGameStore';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DungeonCrawlerState = {
  currentLevel: number;
  keys: number;
  remainingCooldown: number;
  isOpen: boolean;
  /** Gold the player had when they opened the dungeon; used for reward calculations. */
  goldWhenEntered: number;
};

export type DungeonCrawlerActions = {
  openDungeon: (currentGold: number) => void;
  closeDungeon: () => void;

  /** Move to the next dungeon level. */
  advanceLevel: () => void;

  /** Add keys (e.g. from rewards). */
  addKeys: (amount: number) => void;

  /** Consume a key to open a door. Returns false if no keys available. */
  consumeKey: () => boolean;

  /** Tick down the remaining cooldown by 1. */
  tickCooldown: () => void;

  /** Directly set cooldown ticks remaining. */
  setCooldown: (ticks: number) => void;

  /** Add rewards after completing a dungeon run. */
  addRewards: (keys: number) => void;

  /** Reset to initial state (e.g. new game). */
  reset: () => void;
};

export type DungeonCrawlerStore = DungeonCrawlerState & DungeonCrawlerActions;

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialDungeonCrawlerState: DungeonCrawlerState = {
  currentLevel: 0,
  keys: 0,
  remainingCooldown: 0,
  isOpen: false,
  goldWhenEntered: 0,
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

/**
 * Manages the dungeon crawler mini-game state.
 *
 * React usage:
 *   const isOpen = useDungeonCrawlerStore(s => s.isOpen);
 *   const { openDungeon } = useDungeonCrawlerStore();
 *
 * Excalibur / outside React:
 *   const { currentLevel } = useDungeonCrawlerStore.getState();
 *   useDungeonCrawlerStore.subscribe(
 *     s => s.isOpen,
 *     isOpen => { if (isOpen) excaliburScene.goTo('dungeon'); }
 *   );
 */
export const useDungeonCrawlerStore = createGameStore<DungeonCrawlerStore>(
  { saveKey: 'dungeon-crawler' },
  (set, get) => ({
    ...initialDungeonCrawlerState,

    openDungeon: (currentGold) =>
      set({ isOpen: true, goldWhenEntered: currentGold, currentLevel: 0 }),

    closeDungeon: () => set({ isOpen: false }),

    advanceLevel: () => set({ currentLevel: get().currentLevel + 1 }),

    addKeys: (amount) => set({ keys: get().keys + amount }),

    consumeKey: () => {
      const { keys } = get();
      if (keys <= 0) return false;
      set({ keys: keys - 1 });
      return true;
    },

    tickCooldown: () => {
      const { remainingCooldown } = get();
      if (remainingCooldown > 0) {
        set({ remainingCooldown: remainingCooldown - 1 });
      }
    },

    setCooldown: (ticks) => set({ remainingCooldown: ticks }),

    addRewards: (keys) => set({ keys: get().keys + keys }),

    reset: () => set(initialDungeonCrawlerState),
  }),
);
